import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
    Monitor,
    DollarSign,
    Users,
    AlertTriangle,
    ShieldOff,
    Archive,
    RefreshCw,
    Search,
    ChevronDown,
    LayoutGrid as CardsIcon,
    List as TableIcon,
    Eye,
    Edit3,
    Trash2,
    CheckCircle2,
    AlertCircle,
    XCircle,
    MoreHorizontal,
    Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from 'sonner';

interface BusinessSystemsDashboardProps {
    systems: any[];
    loading: boolean;
    refresh: () => void;
    showArchived: boolean;
    setShowArchived: (value: boolean) => void;
}

const BusinessSystemsDashboard: React.FC<BusinessSystemsDashboardProps> = ({ systems, loading, refresh, showArchived, setShowArchived }) => {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [costFilter, setCostFilter] = useState('all');
    const [twoFAFilter, setTwoFAFilter] = useState('all');
    const [archiveTarget, setArchiveTarget] = useState<any>(null);
    const [restoreTarget, setRestoreTarget] = useState<any>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [blockedAction, setBlockedAction] = useState<{ type: 'archive' | 'delete', name: string } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const getIssuesCount = (system: any) => {
        let count = 0;
        if (!system.contractNumber) count++;
        if (system.twoFactorAuth === 'Disabled') count++;
        if (system.totalLicenses > 0 && system.usedLicenses > system.totalLicenses) count++;
        if (!system.vendor) count++;
        return count;
    };

    // Separate active vs archived systems, then apply search & filters
    const activeSystems = systems.filter(s => s.status !== 'Archived');
    const archivedSystems = systems.filter(s => s.status === 'Archived');
    const baseSystems = showArchived ? archivedSystems : activeSystems;

    const displayedSystems = baseSystems.filter(system => {
        // Search
        const matchesSearch = !searchQuery.trim() ||
            system.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            system.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            system.vendor?.toLowerCase().includes(searchQuery.toLowerCase());

        // Status filter
        const matchesStatus = statusFilter === 'all' || system.status?.toLowerCase() === statusFilter.toLowerCase();

        // Category filter
        const matchesCategory = categoryFilter === 'all' || system.category === categoryFilter;

        // Cost filter
        const monthlyCost = parseFloat(system.monthlyCost || 0);
        let matchesCost = true;
        if (costFilter === 'low') matchesCost = monthlyCost > 0 && monthlyCost < 100;
        else if (costFilter === 'medium') matchesCost = monthlyCost >= 100 && monthlyCost < 500;
        else if (costFilter === 'high') matchesCost = monthlyCost >= 500;

        // 2FA filter
        const matches2FA = twoFAFilter === 'all' ||
            (twoFAFilter === 'enabled' && system.twoFactorAuth === 'Enabled') ||
            (twoFAFilter === 'disabled' && system.twoFactorAuth === 'Disabled');

        return matchesSearch && matchesStatus && matchesCategory && matchesCost && matches2FA;
    });

    // Stats are always computed from ALL systems
    const totalMonthlyCost = activeSystems.reduce((acc, system) => acc + (parseFloat(system.monthlyCost) || 0), 0);
    const totalLicenses = activeSystems.reduce((acc, system) => acc + (parseInt(system.totalLicenses) || 0), 0);
    const usedLicenses = activeSystems.reduce((acc, system) => acc + (parseInt(system.usedLicenses) || 0), 0);
    const activeCount = activeSystems.filter(s => s.status === 'Active').length;
    const no2FA = activeSystems.filter(s => s.twoFactorAuth === 'Disabled').length;

    const stats = [
        { label: 'Active Systems', value: activeCount.toString(), icon: <Monitor className="text-blue-600" size={20} />, bg: 'bg-blue-50' },
        { label: 'Monthly Cost', value: `$${totalMonthlyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: <DollarSign className="text-green-600" size={20} />, bg: 'bg-green-50' },
        { label: 'Total Licenses', value: totalLicenses.toString(), icon: <Users className="text-purple-600" size={20} />, bg: 'bg-purple-50' },
        { label: 'Unused Licenses', value: Math.max(0, totalLicenses - usedLicenses).toString(), icon: <AlertTriangle className="text-amber-600" size={20} />, bg: 'bg-amber-50' },
        { label: 'No 2FA', value: no2FA.toString(), icon: <ShieldOff className="text-red-600" size={20} />, bg: 'bg-red-50' },
        { label: 'Archived', value: archivedSystems.length.toString(), icon: <Archive className="text-gray-600" size={20} />, bg: 'bg-gray-50' },
    ];

    const handleArchive = async () => {
        if (!archiveTarget) return;
        try {
            setActionLoading(true);
            await api.put(`/api/business-systems/${archiveTarget.id}`, { status: 'Archived' });
            toast.success(`${archiveTarget.name} has been archived`);
            setArchiveTarget(null);
            refresh();
        } catch (err: any) {
            toast.error(err.message || 'Failed to archive system');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreTarget) return;
        try {
            setActionLoading(true);
            await api.put(`/api/business-systems/${restoreTarget.id}`, { status: 'Active' });
            toast.success(`${restoreTarget.name} has been restored`);
            setRestoreTarget(null);
            refresh();
        } catch (err: any) {
            toast.error(err.message || 'Failed to restore system');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setActionLoading(true);
            await api.delete(`/api/business-systems/${deleteTarget.id}`);
            toast.success(`${deleteTarget.name} has been deleted`);
            setDeleteTarget(null);
            refresh();
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete system');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {stats.map((stat, i) => (
                    <div
                        key={i}
                        className={`bg-white p-4 rounded-2xl border shadow-sm hover:shadow-md transition-all cursor-pointer border-gray-100`}
                        onClick={i === 0 ? () => setShowArchived(false) : i === 5 ? () => setShowArchived(true) : undefined}
                    >
                        <div className="flex justify-between items-start">
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">{stat.label}</p>
                                <h3 className="text-xl font-bold">{stat.value}</h3>
                            </div>
                            <div className={`p-2 rounded-xl ${stat.bg}`}>
                                {stat.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                        placeholder="Search systems..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-gray-50 border-transparent focus:bg-white focus:border-gray-200"
                    />
                </div>

                <div className="grid grid-cols-2 lg:flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full lg:w-[140px] bg-gray-50 border-transparent">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-full lg:w-[150px] bg-gray-50 border-transparent">
                            <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            <SelectItem value="Core Software">Core Software</SelectItem>
                            <SelectItem value="Online Portal">Online Portal</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={costFilter} onValueChange={setCostFilter}>
                        <SelectTrigger className="w-full lg:w-[130px] bg-gray-50 border-transparent">
                            <SelectValue placeholder="All Costs" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Costs</SelectItem>
                            <SelectItem value="low">&lt; $100</SelectItem>
                            <SelectItem value="medium">$100 - $499</SelectItem>
                            <SelectItem value="high">≥ $500</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={twoFAFilter} onValueChange={setTwoFAFilter}>
                        <SelectTrigger className="w-full lg:w-[120px] bg-gray-50 border-transparent">
                            <SelectValue placeholder="All 2FA" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All 2FA</SelectItem>
                            <SelectItem value="enabled">Enabled</SelectItem>
                            <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex bg-gray-100 p-1 rounded-xl">
                    <Button
                        variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                        className={`px-3 h-8 gap-2 rounded-lg ${viewMode === 'table' ? "bg-slate-900 text-white hover:bg-slate-800" : ""}`}
                    >
                        <TableIcon size={16} />
                        <span className="text-xs font-semibold">Table</span>
                    </Button>
                    <Button
                        variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('cards')}
                        className={`px-3 h-8 gap-2 rounded-lg ${viewMode === 'cards' ? "bg-slate-900 text-white hover:bg-slate-800" : ""}`}
                    >
                        <CardsIcon size={16} />
                        <span className="text-xs font-semibold">Cards</span>
                    </Button>
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h4 className="font-bold text-gray-800">
                            {showArchived ? 'Archived Systems' : 'Business Systems'} ({displayedSystems.length})
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                            {showArchived ? 'View and restore archived systems' : 'Manage all business systems and software'}
                        </p>
                    </div>
                    {showArchived ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowArchived(false)}
                            className="rounded-xl border-gray-100 font-bold text-xs gap-2"
                        >
                            <Monitor size={14} />
                            View Active
                        </Button>
                    ) : archivedSystems.length > 0 ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowArchived(true)}
                            className="rounded-xl border-gray-100 font-bold text-xs gap-2"
                        >
                            <Archive size={14} />
                            Archived ({archivedSystems.length})
                        </Button>
                    ) : null}
                </div>

                {loading ? (
                    <div className="p-20 flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                    </div>
                ) : displayedSystems.length === 0 ? (
                    <div className="p-20 flex flex-col items-center justify-center text-center space-y-4">
                        <div className="bg-gray-50 p-4 rounded-full text-gray-400">
                            {showArchived ? <Archive size={48} /> : <Monitor size={48} />}
                        </div>
                        <div>
                            <p className="text-gray-900 font-bold text-lg">
                                {showArchived ? 'No archived systems' : 'No business systems found'}
                            </p>
                            <p className="text-gray-500 text-sm max-w-[300px] mt-1">
                                {showArchived
                                    ? 'Systems you archive will appear here. You can restore them at any time.'
                                    : 'Get started by adding your first software or business system platform.'}
                            </p>
                        </div>
                        {!showArchived && (
                            <Button
                                onClick={() => navigate('/business-systems/new')}
                                className="bg-slate-900 text-white gap-2 px-6"
                            >
                                <Plus size={18} />
                                Add System
                            </Button>
                        )}
                    </div>
                ) : viewMode === 'table' ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50/50 text-[11px] uppercase tracking-wider font-bold text-gray-400">
                                    <th className="px-6 py-4">System Name</th>
                                    <th className="px-6 py-4">Category</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Monthly Cost</th>
                                    <th className="px-6 py-4">License Usage</th>
                                    <th className="px-6 py-4">2FA</th>
                                    <th className="px-6 py-4 text-center">Users</th>
                                    <th className="px-6 py-4 text-center">Issues</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {displayedSystems.map((system) => (
                                    <tr key={system.id} className={`hover:bg-gray-50/50 transition-colors group ${showArchived ? 'opacity-75 hover:opacity-100' : ''}`}>
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{system.name}</p>
                                                <p className="text-[11px] text-gray-400 font-medium line-clamp-1">{system.description}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant="outline" className="rounded-lg bg-gray-50 text-gray-600 border-gray-200 font-bold text-[10px] uppercase">
                                                {system.category}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className={`w-1.5 h-1.5 rounded-full ${system.status === 'Active' ? 'bg-green-500' : system.status === 'Archived' ? 'bg-gray-400' : 'bg-red-500'}`} />
                                                <span className={`ml-2 text-xs font-bold ${system.status === 'Active' ? 'text-green-600' : system.status === 'Archived' ? 'text-gray-500' : 'text-red-600'}`}>{system.status}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-bold text-gray-700">${parseFloat(system.monthlyCost || 0).toFixed(2)}</span>
                                        </td>
                                        <td className="px-6 py-4 min-w-[140px]">
                                            <div className="space-y-1.5">
                                                <div className="flex justify-between text-[10px] font-bold">
                                                    <span className="text-gray-600">{system.usedLicenses}/{system.totalLicenses}</span>
                                                </div>
                                                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${system.totalLicenses > 0 && system.usedLicenses > system.totalLicenses ? 'bg-red-500' : 'bg-slate-900'}`}
                                                        style={{ width: `${system.totalLicenses > 0 ? Math.min(100, (system.usedLicenses / system.totalLicenses) * 100) : 0}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {system.twoFactorAuth === 'Enabled' ? (
                                                <div className="w-5 h-5 rounded-md bg-green-50 flex items-center justify-center text-green-600 mx-auto">
                                                    <CheckCircle2 size={14} />
                                                </div>
                                            ) : system.twoFactorAuth === 'Disabled' ? (
                                                <div className="w-5 h-5 rounded-md bg-red-50 flex items-center justify-center text-red-600 mx-auto">
                                                    <XCircle size={14} />
                                                </div>
                                            ) : (
                                                <span className="text-[10px] font-bold text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-bold text-gray-700">{system.assignedUsers?.length || 0}</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {getIssuesCount(system) > 0 ? (
                                                <div className="flex items-center justify-center gap-1 group/issue cursor-help">
                                                    <AlertCircle size={16} className="text-red-500" />
                                                    <Badge className="bg-red-500 text-white border-0 h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px] font-bold">
                                                        {getIssuesCount(system)}
                                                    </Badge>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center">
                                                    <CheckCircle2 size={16} className="text-green-500" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => navigate(`/business-systems/${system.id}`)}>
                                                    <Eye size={16} />
                                                </Button>
                                                {showArchived ? (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-green-600 hover:bg-green-50"
                                                            onClick={() => setRestoreTarget(system)}
                                                        >
                                                            <RefreshCw size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => {
                                                                if (system.assignedUsers?.length > 0) {
                                                                    setBlockedAction({ type: 'delete', name: system.name });
                                                                } else {
                                                                    setDeleteTarget(system);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => navigate(`/business-systems/${system.id}/edit`)}>
                                                            <Edit3 size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                            onClick={() => {
                                                                if (system.assignedUsers?.length > 0) {
                                                                    setBlockedAction({ type: 'archive', name: system.name });
                                                                } else {
                                                                    setArchiveTarget(system);
                                                                }
                                                            }}
                                                        >
                                                            <Archive size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => {
                                                                if (system.assignedUsers?.length > 0) {
                                                                    setBlockedAction({ type: 'delete', name: system.name });
                                                                } else {
                                                                    setDeleteTarget(system);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {displayedSystems.map((system) => (
                                <div
                                    key={system.id}
                                    className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5 space-y-4 ${showArchived ? 'opacity-75 hover:opacity-100' : ''}`}
                                >
                                    {/* Header */}
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h4 className="text-sm font-bold text-gray-900 truncate">{system.name}</h4>
                                            <p className="text-[11px] text-gray-400 font-medium line-clamp-1 mt-0.5">{system.description}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <div className={`w-2 h-2 rounded-full ${system.status === 'Active' ? 'bg-green-500' : system.status === 'Archived' ? 'bg-gray-400' : 'bg-red-500'}`} />
                                            <span className={`text-[10px] font-bold ${system.status === 'Active' ? 'text-green-600' : system.status === 'Archived' ? 'text-gray-500' : 'text-red-600'}`}>{system.status}</span>
                                        </div>
                                    </div>

                                    {/* Meta Row */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {system.category && (
                                            <Badge variant="outline" className="rounded-lg bg-gray-50 text-gray-600 border-gray-200 font-bold text-[10px] uppercase">
                                                {system.category}
                                            </Badge>
                                        )}
                                        {getIssuesCount(system) > 0 && (
                                            <Badge className="bg-red-500 text-white border-0 rounded-full h-5 px-2 text-[10px] font-bold">
                                                {getIssuesCount(system)} issue{getIssuesCount(system) > 1 ? 's' : ''}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Metrics Grid */}
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cost</p>
                                            <p className="text-sm font-bold text-gray-800 mt-0.5">${parseFloat(system.monthlyCost || 0).toFixed(2)}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Users</p>
                                            <p className="text-sm font-bold text-gray-800 mt-0.5">{system.assignedUsers?.length || 0}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">2FA</p>
                                            <div className="mt-0.5 flex justify-center">
                                                {system.twoFactorAuth === 'Enabled' ? (
                                                    <CheckCircle2 size={16} className="text-green-500" />
                                                ) : system.twoFactorAuth === 'Disabled' ? (
                                                    <XCircle size={16} className="text-red-500" />
                                                ) : (
                                                    <span className="text-[10px] font-bold text-gray-400">N/A</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* License Bar */}
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                            <span>Licenses</span>
                                            <span>{system.usedLicenses}/{system.totalLicenses}</span>
                                        </div>
                                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${system.totalLicenses > 0 && system.usedLicenses > system.totalLicenses ? 'bg-red-500' : 'bg-slate-900'}`}
                                                style={{ width: `${system.totalLicenses > 0 ? Math.min(100, (system.usedLicenses / system.totalLicenses) * 100) : 0}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center justify-end gap-1 pt-1">
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => navigate(`/business-systems/${system.id}`)}>
                                            <Eye size={16} />
                                        </Button>
                                        {showArchived ? (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-green-600 hover:bg-green-50"
                                                    onClick={() => setRestoreTarget(system)}
                                                >
                                                    <RefreshCw size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => {
                                                        if (system.assignedUsers?.length > 0) {
                                                            setBlockedAction({ type: 'delete', name: system.name });
                                                        } else {
                                                            setDeleteTarget(system);
                                                        }
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => navigate(`/business-systems/${system.id}/edit`)}>
                                                    <Edit3 size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                                    onClick={() => {
                                                        if (system.assignedUsers?.length > 0) {
                                                            setBlockedAction({ type: 'archive', name: system.name });
                                                        } else {
                                                            setArchiveTarget(system);
                                                        }
                                                    }}
                                                >
                                                    <Archive size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => {
                                                        if (system.assignedUsers?.length > 0) {
                                                            setBlockedAction({ type: 'delete', name: system.name });
                                                        } else {
                                                            setDeleteTarget(system);
                                                        }
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Archive Confirmation Dialog */}
            <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-gray-600" />
                            Archive this system?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <span className="block">
                                Are you sure you want to archive <strong className="text-gray-700">{archiveTarget?.name}</strong>?
                            </span>
                            <span className="block text-xs text-amber-600 bg-amber-50 p-3 rounded-xl mt-2">
                                ⚠️ This system will be moved to the archived list and will no longer appear in active views. You can restore it at any time.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleArchive}
                            disabled={actionLoading}
                            className="rounded-xl bg-slate-900 hover:bg-slate-800"
                        >
                            {actionLoading ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Restore Confirmation Dialog */}
            <AlertDialog open={!!restoreTarget} onOpenChange={(open) => !open && setRestoreTarget(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <RefreshCw size={18} className="text-green-600" />
                            Restore this system?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="block">
                                Are you sure you want to restore <strong className="text-gray-700">{restoreTarget?.name}</strong>?
                            </span>
                            <span className="block text-xs text-green-600 bg-green-50 p-3 rounded-xl mt-2">
                                ✅ This system will be set back to Active and will appear in your main dashboard.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRestore}
                            disabled={actionLoading}
                            className="rounded-xl bg-green-600 hover:bg-green-700"
                        >
                            {actionLoading ? 'Restoring...' : 'Restore'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 size={18} className="text-red-600" />
                            Delete this system?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="block">
                                Are you sure you want to delete <strong className="text-gray-700">{deleteTarget?.name}</strong>?
                            </span>
                            <span className="block text-xs text-red-600 bg-red-50 p-3 rounded-xl mt-2">
                                🚨 This will delete the system, but historical data will be preserved.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={actionLoading}
                            className="rounded-xl bg-red-600 hover:bg-red-700"
                        >
                            {actionLoading ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Action Blocked Dialog */}
            <AlertDialog open={!!blockedAction} onOpenChange={(open) => !open && setBlockedAction(null)}>
                <AlertDialogContent className="rounded-2xl border-gray-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertCircle size={20} className="text-amber-600" />
                            {blockedAction?.type === 'archive' ? 'Cannot archive system' : 'Cannot delete system'}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 pt-2">
                                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-amber-800 text-sm font-medium">
                                    You cannot {blockedAction?.type} because the system has users, remove the users and try again.
                                </div>
                                <p className="text-gray-500 text-xs px-1 font-medium text-muted-foreground">
                                    All users must be unassigned from <strong className="text-gray-700">{blockedAction?.name}</strong> before this action can be performed.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction
                            onClick={() => setBlockedAction(null)}
                            className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            Got it
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default BusinessSystemsDashboard;
